// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/roles/AdminRolesPage.tsx
//
// Phase 8.2 Wave 7 Task 51 — Admin Roles list page + create/edit/delete CRUD.
//
// Replaces the Phase 3.4a stub (which only listed seeded role types via
// KNOWN_ADMIN_ROLE_TYPES). The new page reads live roles from
// GET /admin/roles, supports custom-role create + rename + permission diff,
// and enforces the delete-guard (cannot remove a role that still
// has active members).
//
// Per CONTEXT.md
//   • Role CRUD with optimistic update + invalidation. TanStack
//                Query keys come from `adminRoleKeys`.
//   • Catalog read from BE (handled by editor dialog).
//   • Custom roles built from Admin perm pool.
//   • Built-in vs custom split, cannot delete role with members.
//                BE returns 409 ROLE_HAS_MEMBERS with `details.member_count`;
//                the confirm dialog quotes the live count.
//   • Permission diff audit (BE-emitted on PATCH).
//   • Colon-notation perm storage (transparent to this page).
//   • At least one role per active admin user; enforced server-side
//                during user PATCH. Surfaced here as the delete-guard.
//
// vg-binding: PLAN:task-51
// vg-binding: P8.D-100
// vg-binding: P8.D-475
// vg-binding: BR-20

import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Button,
  useToast,
  usePermission,
  PageHeader,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  deleteAdminRole,
  extractRoleMemberCount,
  isRoleHasMembersError,
  listAdminRoles,
  type AdminRoleRow,
} from '../../api/admin-roles.api.js';
import { ApiError } from '../../lib/apiClient.js';
import {
  adminRoleKeys,
  invalidateAdminRoleCaches,
} from '../../queries/adminRoleKeys.js';

import { AdminRoleEditorDialog } from '../../features/roles/AdminRoleEditorDialog.js';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type EditorMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; roleId: string; isBuiltIn: boolean };

type DeleteState =
  | { kind: 'closed' }
  | { kind: 'open'; row: AdminRoleRow; submitting: boolean; serverMemberCount?: number };

export function AdminRolesPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('users');
  const queryClient = useQueryClient();

  // Phase 8.2 — `role:read` lists roles; `role:manage` creates/updates/deletes.
  const canRead = usePermission('role:read');
  const canManage = usePermission('role:manage');

  const [editor, setEditor] = useState<EditorMode>({ kind: 'closed' });
  const [deleteState, setDeleteState] = useState<DeleteState>({ kind: 'closed' });

  // ── Fetch roles list ──────────────────────────────────────────────────
  const listQuery = useQuery({
    queryKey: adminRoleKeys.list({ type: 'all' }),
    queryFn: () => listAdminRoles('all'),
    enabled: canRead,
    staleTime: 30_000,
  });

  const rows: AdminRoleRow[] = useMemo(
    () => listQuery.data ?? [],
    [listQuery.data],
  );

  // ── Delete mutation ───────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (roleId: string) => deleteAdminRole(roleId),
    onSuccess: (_, roleId) => {
      toast({
        title: t('role.list.toast.deletedTitle'),
        description: t('role.list.toast.deletedDesc'),
        variant: 'success',
      });
      invalidateAdminRoleCaches(queryClient, roleId);
      setDeleteState({ kind: 'closed' });
    },
    onError: (err: unknown) => {
      // Friendly message for ROLE_HAS_MEMBERS — keep dialog open with the
      // server-reported member_count so the operator sees the live number.
      if (isRoleHasMembersError(err)) {
        const memberCount = extractRoleMemberCount(err);
        setDeleteState((prev) =>
          prev.kind === 'open'
            ? {
                ...prev,
                submitting: false,
                ...(memberCount != null ? { serverMemberCount: memberCount } : {}),
              }
            : prev,
        );
        toast({
          title: t('role.list.toast.cannotDeleteTitle'),
          description:
            memberCount != null
              ? t('role.list.toast.hasMembersWithCount', { count: memberCount })
              : t('role.list.toast.hasMembers'),
          variant: 'warning',
        });
        return;
      }
      const msg = err instanceof ApiError ? err.message : t('role.list.toast.deleteFailFallback');
      toast({ title: t('role.list.toast.deleteFailTitle'), description: msg, variant: 'error' });
      setDeleteState((prev) =>
        prev.kind === 'open' ? { ...prev, submitting: false } : prev,
      );
    },
  });

  // ── Action handlers ───────────────────────────────────────────────────
  function openCreate(): void {
    setEditor({ kind: 'create' });
  }
  function openEdit(row: AdminRoleRow): void {
    setEditor({ kind: 'edit', roleId: row.id, isBuiltIn: row.is_built_in });
  }
  function openDelete(row: AdminRoleRow): void {
    if (row.is_built_in) return; // hard-gate built-ins
    setDeleteState({ kind: 'open', row, submitting: false });
  }
  function confirmDelete(): void {
    if (deleteState.kind !== 'open') return;
    setDeleteState({ ...deleteState, submitting: true });
    deleteMutation.mutate(deleteState.row.id);
  }

  // ── Permission gate ───────────────────────────────────────────────────
  if (!canRead) {
    return (
      <main className="flex flex-col gap-4 p-6">
        <PageHeader
          title={t('role.list.heading')}
          subtitle={t('role.list.subtitleGate')}
        />
        <div className="rounded-md border border-pw-border bg-pw-surface p-4 text-sm text-pw-text-muted">
          {t('role.list.noReadPermission')}
        </div>
      </main>
    );
  }

  // ── Active delete blocker ─────────────────────────────────────────────
  const deletingRow = deleteState.kind === 'open' ? deleteState.row : null;
  const liveMemberCount =
    deleteState.kind === 'open'
      ? deleteState.serverMemberCount ?? deleteState.row.user_count
      : 0;
  const deleteBlocked = liveMemberCount > 0;

  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader
        title={t('role.list.heading')}
        subtitle={t('role.list.subtitle')}
        actions={
          canManage ? (
            <Button
              variant="default"
              data-testid="admin-roles-create-btn"
              onClick={openCreate}
            >
              {t('role.list.createBtn')}
            </Button>
          ) : null
        }
      />

      {!canManage ? (
        <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3 text-sm text-blue-900">
          {t('role.list.readOnlyBanner')}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md border border-pw-border bg-pw-bg">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pw-surface text-left text-xs uppercase tracking-wide text-pw-text-muted">
            <tr>
              <th className="px-4 py-2 font-medium">{t('role.list.columns.name')}</th>
              <th className="px-4 py-2 font-medium">{t('role.list.columns.description')}</th>
              <th className="px-4 py-2 font-medium">{t('role.list.columns.type')}</th>
              <th className="px-4 py-2 font-medium text-right">{t('role.list.columns.permissionCount')}</th>
              <th className="px-4 py-2 font-medium text-right">{t('role.list.columns.memberCount')}</th>
              <th className="px-4 py-2 font-medium text-right">{t('role.list.columns.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-pw-border">
            {listQuery.isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {t('role.list.loading')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-pw-text-muted">
                  {t('role.list.empty')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  data-role-id={row.id}
                  data-testid={`admin-roles-row-${row.code}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-pw-text">{row.name}</div>
                    <div className="font-mono text-[11px] text-pw-text-muted">
                      {row.code}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-pw-text-muted">
                    {row.description ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        row.is_built_in
                          ? 'rounded-full bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300'
                          : 'rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300'
                      }
                    >
                      {row.is_built_in ? t('role.list.typeBuiltIn') : t('role.list.typeCustom')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {row.permission_count}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {row.user_count}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`admin-roles-edit-btn-${row.code}`}
                        onClick={() => openEdit(row)}
                      >
                        {row.is_built_in ? t('role.list.viewPerms') : t('role.list.edit')}
                      </Button>
                      {!row.is_built_in && canManage ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          data-testid={`admin-roles-delete-btn-${row.code}`}
                          disabled={row.user_count > 0}
                          title={
                            row.user_count > 0
                              ? t('role.list.deleteBlockedTitle', { count: row.user_count })
                              : undefined
                          }
                          onClick={() => openDelete(row)}
                        >
                          {t('role.list.delete')}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Editor dialog (create + edit) */}
      {editor.kind !== 'closed' ? (
        <AdminRoleEditorDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditor({ kind: 'closed' });
          }}
          roleId={editor.kind === 'edit' ? editor.roleId : null}
          {...(editor.kind === 'edit'
            ? { isBuiltIn: editor.isBuiltIn }
            : {})}
          onSaved={() => {
            invalidateAdminRoleCaches(queryClient);
          }}
        />
      ) : null}

      {/* Delete confirm */}
      {deletingRow ? (
        <ConfirmDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setDeleteState({ kind: 'closed' });
          }}
          title={t('role.list.confirmDeleteTitle', { name: deletingRow.name })}
          description={
            deleteBlocked
              ? t('role.list.confirmBlockedDesc', { count: liveMemberCount })
              : t('role.list.confirmDeleteDesc', { code: deletingRow.code })
          }
          type="destructive"
          confirmLabel={deleteBlocked ? t('role.list.cannotDelete') : t('role.list.confirmDeleteLabel')}
          cancelLabel={t('common.cancel')}
          loading={
            deleteState.kind === 'open' ? deleteState.submitting : false
          }
          onConfirm={() => {
            if (deleteBlocked) return;
            confirmDelete();
          }}
        />
      ) : null}
    </main>
  );
}

export default AdminRolesPage;
